/**
 * The transactions PayGate sits in the middle of.
 *
 * Every one of these is money or work moving between two parties *through* us:
 * a provider or their EHR submits a claim and we adjudicate it with the partner
 * TPA; clearinghouses feed the same intake as X12 837 batches and MediWorks
 * prices it; the plan share leaves on a real-time rail; the member share is
 * collected from HSA, HRA or card; premium is processed from employers down the
 * client → employer → group → division → location tree, and the commissions
 * those invoices fund are paid out to agents and agencies.
 *
 * So a scene is a pair, not a single node: a source on one side, PayGate in
 * between, a destination on the other. Only the transaction on stage draws a
 * connector — a web of every possible route at once reads as noise — and the
 * line runs source → hub → destination as one continuous path, so the pulse
 * travelling it shows the money passing through us.
 *
 * Sourced from `PayGate-Backend` on `realtime-claims` — the claims engine and
 * its partner integration guide, which is where the X12 837, API key, HMAC, 835
 * and webhook details come from.
 *
 * This module is the data and geometry. It holds no React: cards are positioned
 * as a percentage of `CANVAS` and the connector `<svg>` shares the same
 * viewBox, so the HTML and SVG layers line up at whatever size it renders.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Box extends Point {
  width: number;
  height: number;
}

/** The coordinate space both layers agree on. */
export const CANVAS = { width: 1000, height: 860 } as const;

const CARD = { width: 210, height: 120 } as const;

export const HUB: Box = { x: 350, y: 385, width: 300, height: 165 };

export type EcoIcon =
  | "provider"
  | "clearinghouse"
  | "partner"
  | "adjudication"
  | "employer"
  | "member"
  | "wallet"
  | "rail"
  | "agent";

export interface EcoNode {
  id: string;
  label: string;
  icon: EcoIcon;
  /** Top-left corner, hand-placed in canvas units. */
  x: number;
  y: number;
}

/**
 * The parties, scattered around the hub rather than put on a grid — claims
 * intake up and to the left, where money lands down and to the right. Positions
 * are hand-placed so that every transaction below crosses the hub instead of
 * doubling back along one of its faces.
 */
export const NODES: readonly EcoNode[] = [
  { id: "providers", label: "Providers & EHR", icon: "provider", x: 290, y: 15 },
  { id: "members", label: "Members", icon: "member", x: 640, y: 80 },
  { id: "clearinghouses", label: "Clearinghouses", icon: "clearinghouse", x: 20, y: 305 },
  { id: "partnerTpas", label: "Partner TPAs", icon: "partner", x: 720, y: 300 },
  { id: "employers", label: "Employers", icon: "employer", x: 25, y: 470 },
  { id: "mediworks", label: "Benefit Logistix", icon: "adjudication", x: 735, y: 470 },
  { id: "healthone", label: "HealthOne HSA", icon: "wallet", x: 130, y: 645 },
  { id: "paymentRails", label: "Payment Rails", icon: "rail", x: 405, y: 690 },
  { id: "agents", label: "Agents & agencies", icon: "agent", x: 665, y: 640 },
];

export interface EcoTransaction {
  id: string;
  /** The caption shown on the hub while this transaction is on stage. */
  label: string;
  from: string;
  to: string;
}

export const TRANSACTIONS: readonly EcoTransaction[] = [
  { id: "claims", label: "Real-time claims", from: "providers", to: "partnerTpas" },
  { id: "intake", label: "Clearinghouse intake", from: "clearinghouses", to: "mediworks" },
  { id: "payouts", label: "Provider payouts", from: "partnerTpas", to: "paymentRails" },
  { id: "member", label: "Member responsibility", from: "members", to: "healthone" },
  { id: "premium", label: "Premium processed", from: "employers", to: "paymentRails" },
  { id: "commission", label: "Commissions paid", from: "employers", to: "agents" },
];

export function nodeById(id: string): EcoNode {
  const node = NODES.find((n) => n.id === id);
  if (!node) throw new Error(`unknown ecosystem node: ${id}`);
  return node;
}

/** The card rectangle for a node, in canvas units. */
export function nodeBox(node: EcoNode): Box {
  return { x: node.x, y: node.y, width: CARD.width, height: CARD.height };
}

/** A box as CSS percentages of the canvas, for absolute positioning. */
export function boxStyle(box: Box): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  return {
    left: `${round((box.x / CANVAS.width) * 100)}%`,
    top: `${round((box.y / CANVAS.height) * 100)}%`,
    width: `${round((box.width / CANVAS.width) * 100)}%`,
    height: `${round((box.height / CANVAS.height) * 100)}%`,
  };
}

function center(box: Box): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/** Keeps a hub attachment point off the hub's own rounded corners. */
const HUB_INSET = 28;

const CORNER_RADIUS = 20;

/**
 * Which face of the hub a card talks to, and where each end of the run sits.
 *
 * A card clear above or below the hub meets it vertically; anything level with
 * it comes in from the side. The hub end is nudged toward the card so two runs
 * on the same face do not land on the same point.
 */
function faceFor(node: EcoNode): { card: Point; hub: Point; vertical: boolean } {
  const box = nodeBox(node);
  const cardMid = center(box);

  if (box.y + box.height <= HUB.y) {
    return {
      card: { x: cardMid.x, y: box.y + box.height },
      hub: { x: clamp(cardMid.x, HUB.x + HUB_INSET, HUB.x + HUB.width - HUB_INSET), y: HUB.y },
      vertical: true,
    };
  }
  if (box.y >= HUB.y + HUB.height) {
    return {
      card: { x: cardMid.x, y: box.y },
      hub: {
        x: clamp(cardMid.x, HUB.x + HUB_INSET, HUB.x + HUB.width - HUB_INSET),
        y: HUB.y + HUB.height,
      },
      vertical: true,
    };
  }
  const left = box.x + box.width <= HUB.x;
  return {
    card: { x: left ? box.x + box.width : box.x, y: cardMid.y },
    hub: {
      x: left ? HUB.x : HUB.x + HUB.width,
      y: clamp(cardMid.y, HUB.y + HUB_INSET, HUB.y + HUB.height - HUB_INSET),
    },
    vertical: false,
  };
}

/** The run between a card and the hub, turning once at the halfway point. */
function runToHub(node: EcoNode): Point[] {
  const { card, hub, vertical } = faceFor(node);
  if (vertical) {
    const midY = (card.y + hub.y) / 2;
    return [card, { x: card.x, y: midY }, { x: hub.x, y: midY }, hub];
  }
  const midX = (card.x + hub.x) / 2;
  return [card, { x: midX, y: card.y }, { x: midX, y: hub.y }, hub];
}

/**
 * The whole transaction as one path: out of the source, through the hub, into
 * the destination. The leg across the hub's own footprint is hidden behind the
 * hub card, and keeping it in the path is what lets a single pulse travel the
 * transaction end to end.
 */
export function transactionPath(transaction: EcoTransaction): string {
  const into = runToHub(nodeById(transaction.from));
  const outOf = runToHub(nodeById(transaction.to));
  return roundedPolyline([...into, center(HUB), ...outOf.reverse()]);
}

/** Where a transaction starts and ends, for orienting its gradient. */
export function transactionEnds(transaction: EcoTransaction): { from: Point; to: Point } {
  return {
    from: faceFor(nodeById(transaction.from)).card,
    to: faceFor(nodeById(transaction.to)).card,
  };
}

/**
 * A path through `points` with the corners rounded.
 *
 * Consecutive duplicates and points that only continue a straight run are
 * dropped first, so a card that lines up with the hub yields a straight line
 * rather than a corner with no length to round.
 */
export function roundedPolyline(points: readonly Point[], radius = CORNER_RADIUS): string {
  const pts = simplify(points);
  if (pts.length === 0) return "";

  const first = pts[0]!;
  if (pts.length === 1) return `M ${fmt(first.x)} ${fmt(first.y)}`;

  let d = `M ${fmt(first.x)} ${fmt(first.y)}`;

  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1]!;
    const corner = pts[i]!;
    const next = pts[i + 1]!;
    // Never eat more than half of either leg, or a short segment inverts.
    const r = Math.min(radius, distance(prev, corner) / 2, distance(corner, next) / 2);
    const entry = towards(corner, prev, r);
    const exit = towards(corner, next, r);
    d +=
      ` L ${fmt(entry.x)} ${fmt(entry.y)}` +
      ` Q ${fmt(corner.x)} ${fmt(corner.y)} ${fmt(exit.x)} ${fmt(exit.y)}`;
  }

  const last = pts[pts.length - 1]!;
  return `${d} L ${fmt(last.x)} ${fmt(last.y)}`;
}

// ─── geometry helpers ───────────────────────────────────────────────────────

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function simplify(points: readonly Point[]): Point[] {
  const out: Point[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && same(last, p)) continue;
    const before = out[out.length - 2];
    if (before && last && collinear(before, last, p)) out.pop();
    out.push(p);
  }
  return out;
}

function same(a: Point, b: Point): boolean {
  return Math.abs(a.x - b.x) < 0.01 && Math.abs(a.y - b.y) < 0.01;
}

function collinear(a: Point, b: Point, c: Point): boolean {
  return Math.abs((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)) < 0.01;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** The point `d` along the line from `from` towards `to`. */
function towards(from: Point, to: Point, d: number): Point {
  const len = distance(from, to);
  if (len === 0) return { ...from };
  return { x: from.x + ((to.x - from.x) * d) / len, y: from.y + ((to.y - from.y) * d) / len };
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
